import React, { useState, useEffect, useRef } from 'react';

/**
 * SlotDigit — single character with vertical slot-machine roll animation.
 * Non-digit characters (€, :, ,, .) are rendered statically.
 */
function SlotDigit({ char, heightEm }) {
  const isDigit   = /\d/.test(char);
  const prevRef   = useRef(char);
  const [cur, setCur]       = useState(char);
  const [rolling, setRolling] = useState(false);

  useEffect(() => {
    if (char === prevRef.current) return;
    prevRef.current = char;

    setRolling(true);
    setCur(char);

    const t = setTimeout(() => setRolling(false), 450);
    return () => clearTimeout(t);
  }, [char]);

  if (!isDigit) {
    return (
      <span
        style={{
          display: 'inline-block',
          lineHeight: `${heightEm}em`,
          userSelect: 'none',
        }}
      >
        {char}
      </span>
    );
  }

  const n = parseInt(cur, 10);

  return (
    <span
      style={{
        display: 'inline-block',
        overflow: 'hidden',
        height: `${heightEm}em`,
        verticalAlign: 'bottom',
        userSelect: 'none',
      }}
    >
      <span
        style={{
          display: 'flex',
          flexDirection: 'column',
          transform: `translateY(calc(-${n} * ${heightEm}em))`,
          transition: rolling
            ? 'transform 0.42s cubic-bezier(0.22, 1, 0.36, 1)'
            : 'none',
          willChange: 'transform',
        }}
      >
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(d => (
          <span
            key={d}
            style={{
              display: 'block',
              height: `${heightEm}em`,
              lineHeight: `${heightEm}em`,
              textAlign: 'center',
            }}
          >
            {d}
          </span>
        ))}
      </span>
    </span>
  );
}

/**
 * SlotNumber
 *
 * @param {string|number} value   — the value to display (e.g. "02:15:43" or "499,00 €")
 * @param {string}        className
 * @param {object}        style
 * @param {number}        heightEm — height of each digit cell in em (default 1.2)
 *
 * Usage:
 *   <SlotNumber value={formatPrice(currentPrice)} className="text-3xl font-bold" />
 *   <SlotNumber value={formatted}                 className="text-sm font-mono font-bold" />
 */
export default function SlotNumber({ value, className, style, heightEm = 1.2 }) {
  const str = String(value ?? '');

  return (
    <span
      className={className}
      style={{ display: 'inline-flex', alignItems: 'flex-end', ...style }}
    >
      {str.split('').map((char, i) => (
        <SlotDigit key={str.length - 1 - i + '-' + i} char={char} heightEm={heightEm} />
      ))}
    </span>
  );
}
